import { create } from 'zustand';
import { getModalStatus, trackModal } from '@/services/user.service';
import { getPromotionalCheckout, getNewUserCheckout } from '@/services/plans.service';
import { getActiveCampaignParam, isLandingPageCampaign, clearStoredCampaignParam } from '@/utils/campaignUtils';
import { useUserStore } from '@/stores/user/userStore';
// Helper function to check if user account was created within last 24 hours
// For login flows only - signup flows should always show the modal regardless of account age
const isAccountWithin24Hours = (isLoginFlow = true) => {
  // For signup flows, always return true (no 24-hour restriction)
  if (!isLoginFlow) {
    return true;
  }

  const user = useUserStore.getState().user;

  if (!user || !user.date_created) {
    return false;
  }

  const accountCreatedAt = new Date(user.date_created);
  const now = new Date();
  const hoursSinceCreation = (now - accountCreatedAt) / (1000 * 60 * 60);

  return hoursSinceCreation <= 24;
};

const useWelcomeBackModalStore = create((set, get) => ({
  // Modal state
  isOpen: false,
  isLoading: false,
  modalStatus: null,
  // Modal status data
  modalShownCount: 0,
  limitReached: false,
  maxShows: 5,
  eligibleForPromotion: false,

  isCampaignModal: false,
  campaignParam: null,
  
  // Actions
  setIsOpen: (isOpen) => set({ isOpen }),
  setIsLoading: (isLoading) => set({ isLoading }),
  setModalStatus: (modalStatus) => set({ modalStatus }),
  setIsCampaignModal: (isCampaignModal) => set({ isCampaignModal }),
  setCampaignParam: (campaignParam) => set({ campaignParam }),
  
  // Check if modal should be shown
  checkModalStatus: async () => {
    try {
      set({ isLoading: true });

      const response = await getModalStatus();
      const data = response.data;

      set({
        modalStatus: data,
        modalShownCount: data.modal_shown_count,
        limitReached: data.limit_reached,
        maxShows: data.max_shows,
        eligibleForPromotion: data.eligible_for_promotion,
        isLoading: false
      });

      // Show modal if conditions are met
      const shouldShow = !data.limit_reached && data.eligible_for_promotion;
      if (shouldShow) {
        set({ isOpen: true });
      }

      return shouldShow;
    } catch (error) {
      console.error('Error checking modal status:', error);
      set({ isLoading: false });
      return false;
    }
  },

  // Show campaign modal for landing page campaign
  showCampaignModal: () => {
    const campaignParam = getActiveCampaignParam();

    if (isLandingPageCampaign(campaignParam)) {
      
    
      const auth = useUserStore.getState().auth;

      // (login_count = 1 means first login after signup, > 1 means returning user)
      const isLoginFlow = auth?.login_count > 1;

      if (isAccountWithin24Hours(isLoginFlow)) {
        set({
          isOpen: true,
          isCampaignModal: true,
          campaignParam: campaignParam
        });
        return true;
      } else {
        clearStoredCampaignParam();
        return false;
      }
    }
    return false;
  },
  
  // Track modal interaction
  trackModalInteraction: async () => {
    try {
      await trackModal();

      // Update local count
      const currentCount = get().modalShownCount;
      set({ modalShownCount: currentCount + 1 });
    } catch (error) {
      console.error('Error tracking modal:', error);
    }
  },
  
  // Handle accept offer
  handleAcceptOffer: async () => {
    try {
      set({ isLoading: true });
      const state = get();

      let response;

      // Use different checkout endpoint based on modal type
      if (state.isCampaignModal && state.campaignParam) {
        try {
          response = await getNewUserCheckout(state.campaignParam);
        } catch (newUserError) {
        
          set({ isLoading: false });
          throw newUserError; 
        }
      } else {

        response = await getPromotionalCheckout();
      }

      const checkoutUrl = response.data.checkout_session.url;

      // Track modal interaction (only for non-campaign modals)
      if (!state.isCampaignModal) {
        await get().trackModalInteraction();
      }

      if (checkoutUrl) {
        window.open(checkoutUrl, '_blank');
      }

      if (state.isCampaignModal) {
        clearStoredCampaignParam();
      }

      // Close modal
      set({
        isOpen: false,
        isLoading: false,
        isCampaignModal: false,
        campaignParam: null
      });
    } catch (error) {
      console.error('Error handling accept offer:', error);
      set({ isLoading: false });
    }
  },
  
  // Handle modal close
  handleClose: async () => {
    try {
      const state = get();

      // Track the modal interaction (only for non-campaign modals)
      if (!state.isCampaignModal) {
        await get().trackModalInteraction();
      }
      if (state.isCampaignModal) {
        clearStoredCampaignParam();
      }

      // Close modal
      set({
        isOpen: false,
        isCampaignModal: false,
        campaignParam: null
      });
    } catch (error) {
      console.error('Error handling modal close:', error);
      set({
        isOpen: false,
        isCampaignModal: false,
        campaignParam: null
      });
    }
  },
  
  // Reset store
  reset: () => set({
    isOpen: false,
    isLoading: false,
    modalStatus: null,
    modalShownCount: 0,
    limitReached: false,
    maxShows: 5,
    eligibleForPromotion: false,
    isCampaignModal: false,
    campaignParam: null
  })
}));

export default useWelcomeBackModalStore;
